import type { MotifGlyphKind } from "./MotifGlyph";

export type BoardMotifAnnotation = {
  kind: MotifGlyphKind;
  label: string;
  title: string;
  color: string;
  evidenceKind?: "tactical";
  motif?: string;
  targetSquares?: readonly string[];
};

export const BOARD_TACTICAL_ANNOTATIONS: Record<string, BoardMotifAnnotation> = {
  engine_opportunity: {
    kind: "engine_opportunity",
    label: "TO",
    title: "Tactical Opportunity",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  missed_check: {
    kind: "missed_check",
    label: "M+",
    title: "Missed Check",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  missed_capture: {
    kind: "missed_capture",
    label: "MC",
    title: "Missed Capture",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  hanging_piece: {
    kind: "hanging_piece",
    label: "HP",
    title: "Hanging Piece",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  fork: { kind: "fork", label: "F", title: "Fork", color: "#ff3d12", evidenceKind: "tactical" },
  pin: { kind: "pin", label: "P", title: "Pin", color: "#ff3d12", evidenceKind: "tactical" },
  skewer: { kind: "skewer", label: "S", title: "Skewer", color: "#ff3d12", evidenceKind: "tactical" },
  discovered_attack: {
    kind: "discovered_attack",
    label: "DA",
    title: "Discovered Attack",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  back_rank_threat: {
    kind: "back_rank_threat",
    label: "BR",
    title: "Back-Rank Threat",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
  deflection: { kind: "deflection", label: "D", title: "Deflection", color: "#ff3d12", evidenceKind: "tactical" },
  sacrifice: { kind: "sacrifice", label: "S!", title: "Sacrifice", color: "#ff3d12", evidenceKind: "tactical" },
  zwischenzug: { kind: "zwischenzug", label: "ZZ", title: "Zwischenzug", color: "#ff3d12", evidenceKind: "tactical" },
  mating_net: { kind: "mating_net", label: "MN", title: "Mating Net", color: "#ff3d12", evidenceKind: "tactical" },
  overloaded_defender: {
    kind: "overloaded_defender",
    label: "OD",
    title: "Overloaded Defender",
    color: "#ff3d12",
    evidenceKind: "tactical",
  },
};
export const boardMotifAnnotation = (input: string[]): BoardMotifAnnotation | null => {
  const motifs = input;
  if (motifs.includes("book")) return { kind: "book", label: "book", title: "Book move", color: "#c9a869" };
  if (motifs.includes("checkmate")) return { kind: "text", label: "#", title: "Checkmate", color: "#f0ece2" };
  if (motifs.includes("win_by_resignation"))
    return { kind: "flag", label: "flag", title: "Won by resignation", color: "#f0ece2" };
  if (motifs.includes("win_on_time")) return { kind: "clock", label: "clock", title: "Won on time", color: "#f0ece2" };
  if (motifs.includes("brilliant"))
    return { kind: "brilliant", label: "!!", title: "Brilliant move", color: "#22b8cf" };
  if (motifs.includes("best")) return { kind: "best", label: "best", title: "Best move", color: "#81b64c" };
  if (motifs.includes("excellent")) return { kind: "check", label: "✓", title: "Excellent move", color: "#5f9f45" };
  if (motifs.includes("good")) return { kind: "check", label: "✓", title: "Good move", color: "#96a1a8" };
  if (motifs.includes("blunder") || motifs.includes("opponent_blunder")) {
    return { kind: "text", label: "??", title: "Blunder", color: "#ff3d00" };
  }
  if (motifs.includes("mistake") || motifs.includes("opponent_mistake")) {
    return { kind: "text", label: "?", title: "Mistake", color: "#f97316" };
  }
  if (motifs.includes("imprecision") || motifs.includes("opponent_imprecision")) {
    return { kind: "text", label: "?!", title: "Inaccuracy", color: "#eab308" };
  }
  if (motifs.includes("time_pressure") || motifs.includes("low_time")) {
    return { kind: "clock", label: "clock", title: "Time pressure", color: "#f59e0b" };
  }
  return null;
};

export const boardEvidenceMotifAnnotation = (motifs: string[]): BoardMotifAnnotation | null => {
  const classification = boardMotifAnnotation(motifs);
  if (classification) return classification;
  const tacticalMotif = motifs.find((motif) => BOARD_TACTICAL_ANNOTATIONS[motif]);
  return tacticalMotif ? BOARD_TACTICAL_ANNOTATIONS[tacticalMotif] : null;
};

export const boardMotifColor = (motifs: string[]): string => boardMotifAnnotation(motifs)?.color ?? "#f0ece2";
